import {
  ReactElement,
  ChangeEvent,
  useMemo,
  useCallback,
  useState,
} from 'react';
import { Typography, Stack, FormControlLabel, Collapse } from '@mui/material';
import {
  ChangeCircle as ChangeStateIcon,
  ManageAccounts as ModifyAssignmentIcon,
} from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileArrowDown as ExportListIcon,
  faBarcode as BardCodeIcon,
  faClone as DuplicateIcon,
  faUserTie as DriverIcon,
  faVectorSquare as LogisticZoneIcon,
  faStoreAlt as CompanyIcon,
  faChartBar as TrackingIcon,
} from '@fortawesome/free-solid-svg-icons';
import { useSnackbar } from 'notistack';
import moment from 'moment';

import {
  useAppSelector,
  useLazyRequest,
  useExportTickets,
  useExportItemsInProgress,
  useToggle,
} from '~hooks/index';
import { selectAuth } from '~store/slices/auth/index';
import { isOnlyReadUser, isOnlyCreatorOrder } from '~utils/user';
import { ItemFilterV2Base } from '~services/item/types';
import { searchItemsToExportV2, duplicateItems } from '~services/item';
import {
  ActionOrderMenu,
  DialogChangeOrderState,
  DialogModifyAssignmentOrders,
  DialogConfimation,
} from '~components/index';
import { ActionOrderMenuOptionItem } from '~components/ActionOrderMenu/types';
import { Nullable } from '~globals/types/commons';
import {
  PlanFeaturesTypes,
  ReportItemsInProgressMode,
} from '~globals/types/enums';
import {
  getFeatureAvailableToDate,
  getLimitPremiunByVersion,
} from '~utils/feature';
import { formatterDate } from '~utils/formatter';
import { downloadFileByRequest } from '~utils/download';

// eslint-disable-next-line max-len
import { useDashboardVariantV2CarrierInTransitFiltersContext } from '../../../../../InTransitFiltersContext';
import { useDashboardVariantV2CarrierPresetsContext } from '../../../../../PresetsContext';
import { useDashboardVariantV2CarrierContentInTransitTabContext } from '../../Context';

import {
  DashboardVariantV2CarrierContentActionsContainer,
  DashboardVariantV2CarrierContentAccessFeatureButtonSwitch,
  DashboardVariantV2CarrierContentButtonSwitchInput,
  DashboardVariantV2CarrierContentFormControlWithBackground,
  DashboardVariantV2CarrierContentFormControlCheckBox,
} from '../../../../styles';

const DashboardVariantV2CarrierContentInTransitTabActions =
  (): ReactElement => {
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAppSelector(selectAuth);

    const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);
    const isUserOnlyCreatorOrder = useMemo(
      () => isOnlyCreatorOrder(user),
      [user],
    );

    const {
      filters: { handleClearFilters, handleClearFilterByValues },
      forceRequest: { toggleValue: toggleForceRequest },
    } = useDashboardVariantV2CarrierInTransitFiltersContext();

    const {
      selectedPreset: { reset: handleResetSelectedPreset },
    } = useDashboardVariantV2CarrierPresetsContext();

    const {
      selection: { selectedIds, utils: selectedIdsUtils },
      parseFiltersRequest,
      table: {
        queryParams: { pagination },
      },
      showTracking: { value: showTracking, toggleValue: toggleShowTracking },
      isOnlyTodayDelivery: {
        value: isOnlyTodayDelivery,
        toggleValue: toggleIsOnlyTodayDelivery,
      },
      actions: { reloadDataAndResetSelectedRows },
    } = useDashboardVariantV2CarrierContentInTransitTabContext();

    const isEmptySelectedIds = useMemo(
      () => selectedIdsUtils.isEmpty(),
      [selectedIdsUtils],
    );

    const handleChangeIsTodayDelivery = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target ?? event.currentTarget;
        toggleIsOnlyTodayDelivery(checked);

        let expirationDateTimeFrom: Nullable<Date> = null;
        let expirationDateTimeTo: Nullable<Date> = null;

        if (checked) {
          const currentDate = moment();
          expirationDateTimeFrom = currentDate.startOf('day').toDate();
          expirationDateTimeTo = currentDate.clone().endOf('day').toDate();
        }

        handleClearFilters(false);
        handleClearFilterByValues(
          { expirationDateTimeFrom, expirationDateTimeTo },
          () => toggleForceRequest(!showTracking),
        );
        handleResetSelectedPreset();
      },
      [
        handleClearFilterByValues,
        handleClearFilters,
        handleResetSelectedPreset,
        showTracking,
        toggleForceRequest,
        toggleIsOnlyTodayDelivery,
      ],
    );

    const [, loadingSearchToExport, , executeSearchToExport] = useLazyRequest({
      request: searchItemsToExportV2,
      withPostSuccess: (response) => {
        if (response.headers && response.data) {
          const bodyRequest = JSON.parse(
            response.config?.data as string,
          ) as ItemFilterV2Base;

          let currentDate = formatterDate(new Date(), { format: 'YYYYMMDD' });

          if (bodyRequest.dateFrom && bodyRequest.dateTo) {
            const dateFromDownload = formatterDate(bodyRequest.dateFrom, {
              format: 'YYYYMMDD',
            });

            const dateToDownload = formatterDate(bodyRequest.dateTo, {
              format: 'YYYYMMDD',
            });

            currentDate = `${dateFromDownload}-${dateToDownload}`;
          }

          const defaultFileName = `pedidos-en-transito-${currentDate}.csv`;

          downloadFileByRequest(
            response.headers,
            response.data,
            defaultFileName,
          );

          enqueueSnackbar(`Se ha exportado los pedidos correctamente`, {
            variant: 'success',
          });
        } else {
          enqueueSnackbar(
            'No se pudo exportar los pedidos, intente nuevamente',
            { variant: 'error' },
          );
        }
      },
      withPostFailure: () => {
        enqueueSnackbar('No se pudo exportar los pedidos, intente nuevamente', {
          variant: 'error',
        });
      },
    });

    const handleExportItems = useCallback(async () => {
      await executeSearchToExport({
        filters: parseFiltersRequest(),
        pagination,
      });
    }, [executeSearchToExport, pagination, parseFiltersRequest]);

    const { loading: loadingExportTickets, onExportTickets } =
      useExportTickets();

    const handleExportTickets = useCallback(async () => {
      if (!isEmptySelectedIds) {
        await onExportTickets({
          itemIds: selectedIds,
        });
      }
    }, [isEmptySelectedIds, selectedIds, onExportTickets]);

    const [openDialogChangeStateOrders, toggleOpenDialogChangeStateOrders] =
      useToggle(false);

    const [
      openDialogModifyAssignmentOrders,
      toggleDialogModifyAssignmentOrders,
    ] = useToggle(false);

    const [openConfirmDuplicateOrders, toggleOpenConfirmDuplicateOrders] =
      useToggle(false);

    const actionsWitSelectionMenuOptions = useMemo<ActionOrderMenuOptionItem[]>(
      () => [
        {
          label: 'Cambiar estado de pedidos',
          Icon: <ChangeStateIcon color="success" />,
          enabled: !isUserOnlyReader,
          onClick: () => toggleOpenDialogChangeStateOrders(true),
        },
        {
          label: 'Modificar asignación de pedidos',
          Icon: <ModifyAssignmentIcon color="success" />,
          enabled: !isUserOnlyReader,
          onClick: () => toggleDialogModifyAssignmentOrders(true),
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.LimitNotPro,
          },
        },
        {
          label: 'Exportar etiquetas',
          Icon: <FontAwesomeIcon icon={BardCodeIcon} />,
          loading: loadingExportTickets,
          enabled: true,
          onClick: handleExportTickets,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.MgmGenerateTickets,
            availableToDate: getFeatureAvailableToDate(
              PlanFeaturesTypes.MgmGenerateTickets,
            ),
          },
        },
        {
          label: 'Repetir destinos',
          Icon: <FontAwesomeIcon icon={DuplicateIcon} />,
          enabled: !isUserOnlyCreatorOrder,
          onClick: () => toggleOpenConfirmDuplicateOrders(true),
        },
      ],
      [
        isUserOnlyReader,
        isUserOnlyCreatorOrder,
        loadingExportTickets,
        handleExportTickets,
        toggleOpenDialogChangeStateOrders,
        toggleDialogModifyAssignmentOrders,
        toggleOpenConfirmDuplicateOrders,
      ],
    );

    const actionsGeneralMenuOptionsNormal = useMemo<
      ActionOrderMenuOptionItem[]
    >(
      () => [
        {
          label: 'Exportar listado',
          Icon: <FontAwesomeIcon icon={ExportListIcon} />,
          loading: loadingSearchToExport,
          enabled: true,
          disabled: false,
          onClick: handleExportItems,
        },
      ],
      [loadingSearchToExport, handleExportItems],
    );

    const { loading: loadingExportItemsInProgress, onExportItemsInProgress } =
      useExportItemsInProgress();
    const [
      currentModeExportItemsInProgress,
      setCurrentModeExportItemsInProgress,
    ] = useState<Nullable<ReportItemsInProgressMode>>(null);

    const getIsLoadingExportItemsInProgressByMode = useCallback(
      (mode: ReportItemsInProgressMode) =>
        loadingExportItemsInProgress &&
        currentModeExportItemsInProgress === mode,
      [currentModeExportItemsInProgress, loadingExportItemsInProgress],
    );

    const handleExportItemsInProgress = useCallback(
      async (mode: ReportItemsInProgressMode) => {
        setCurrentModeExportItemsInProgress(mode);

        await onExportItemsInProgress({
          reportItemsInProgressMode: mode,
          onlyTodayDelivery: isOnlyTodayDelivery,
        });

        setCurrentModeExportItemsInProgress(null);
      },
      [onExportItemsInProgress, isOnlyTodayDelivery],
    );

    const actionsGeneralMenuOptionsTracking = useMemo<
      ActionOrderMenuOptionItem[]
    >(
      () => [
        {
          label: 'Descargar seguimiento por choferes',
          Icon: <FontAwesomeIcon icon={DriverIcon} />,
          loading: getIsLoadingExportItemsInProgressByMode(
            ReportItemsInProgressMode.Drivers,
          ),
          enabled: true,
          disabled: false,
          onClick: () =>
            handleExportItemsInProgress(ReportItemsInProgressMode.Drivers),
        },
        {
          label: 'Descargar seguimiento por zonas',
          Icon: <FontAwesomeIcon icon={LogisticZoneIcon} />,
          loading: getIsLoadingExportItemsInProgressByMode(
            ReportItemsInProgressMode.Zones,
          ),
          enabled: true,
          disabled: false,
          onClick: () =>
            handleExportItemsInProgress(ReportItemsInProgressMode.Zones),
        },
        {
          label: 'Descargar seguimiento por empresas',
          Icon: <FontAwesomeIcon icon={CompanyIcon} />,
          loading: getIsLoadingExportItemsInProgressByMode(
            ReportItemsInProgressMode.Companies,
          ),
          enabled: true,
          disabled: false,
          onClick: () =>
            handleExportItemsInProgress(ReportItemsInProgressMode.Companies),
        },
      ],
      [getIsLoadingExportItemsInProgressByMode, handleExportItemsInProgress],
    );

    const actionsGeneralMenuOptions = useMemo<ActionOrderMenuOptionItem[]>(
      () =>
        showTracking
          ? actionsGeneralMenuOptionsTracking
          : actionsGeneralMenuOptionsNormal,
      [
        actionsGeneralMenuOptionsNormal,
        actionsGeneralMenuOptionsTracking,
        showTracking,
      ],
    );

    const handleSuccessChangeOrdersState = useCallback(() => {
      toggleOpenDialogChangeStateOrders(false);

      reloadDataAndResetSelectedRows();
    }, [reloadDataAndResetSelectedRows, toggleOpenDialogChangeStateOrders]);

    const [, loadingDuplicateOrders, , executeDuplicateOrders] = useLazyRequest(
      {
        request: duplicateItems,
        withPostSuccess: () => {
          enqueueSnackbar('Destinos repetidos correctamente', {
            variant: 'success',
          });

          toggleOpenConfirmDuplicateOrders();

          reloadDataAndResetSelectedRows();
        },
        withPostFailure: () => {
          enqueueSnackbar(
            'No se pudo repetir los destinos, intente nuevamente',
            { variant: 'error' },
          );
        },
      },
    );

    const handleDuplicateOrders = useCallback(() => {
      if (!isEmptySelectedIds) {
        executeDuplicateOrders(selectedIds);
      }
    }, [executeDuplicateOrders, isEmptySelectedIds, selectedIds]);

    return (
      <DashboardVariantV2CarrierContentActionsContainer justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography component="h6" variant="h6" fontWeight="bold">
            PEDIDOS EN TRÁNSITO
          </Typography>

          {!isUserOnlyReader && (
            <Collapse
              in={!isEmptySelectedIds && !showTracking}
              timeout="auto"
              unmountOnExit
              orientation="horizontal"
            >
              <ActionOrderMenu
                options={actionsWitSelectionMenuOptions}
                buttonProps={{ label: 'Selección' }}
              />
            </Collapse>
          )}
        </Stack>

        <Stack direction="row" spacing={2}>
          <DashboardVariantV2CarrierContentFormControlWithBackground
            control={
              <DashboardVariantV2CarrierContentFormControlCheckBox
                checked={isOnlyTodayDelivery}
                onChange={handleChangeIsTodayDelivery}
              />
            }
            label="Solo envíos que vencen hoy"
          />

          {!isUserOnlyReader && (
            <>
              <ActionOrderMenu options={actionsGeneralMenuOptions} />

              <DashboardVariantV2CarrierContentAccessFeatureButtonSwitch
                validFeature={PlanFeaturesTypes.LimitPremium}
                availableToDate={getLimitPremiunByVersion('1.22.3')}
                activeForVersion="1.22.3"
                variant="outlined"
                color="primary"
                startIcon={<FontAwesomeIcon icon={TrackingIcon} />}
                onClick={() => toggleShowTracking()}
                disableRipple
                disableFocusRipple
                disableTouchRipple
                sx={{ borderColor: 'transparent' }}
              >
                <FormControlLabel
                  control={
                    <DashboardVariantV2CarrierContentButtonSwitchInput
                      checked={showTracking}
                    />
                  }
                  label="Ver Seguimiento"
                  labelPlacement="start"
                  componentsProps={{
                    typography: {
                      variant: 'inherit',
                    },
                  }}
                  sx={{ marginLeft: 0 }}
                />
              </DashboardVariantV2CarrierContentAccessFeatureButtonSwitch>
            </>
          )}
        </Stack>

        <DialogChangeOrderState
          open={openDialogChangeStateOrders}
          onClose={() => toggleOpenDialogChangeStateOrders(false)}
          itemsIds={selectedIds}
          onChangeSuccess={handleSuccessChangeOrdersState}
        />

        {openDialogModifyAssignmentOrders && (
          <DialogModifyAssignmentOrders
            open={openDialogModifyAssignmentOrders}
            onClose={() => toggleDialogModifyAssignmentOrders(false)}
            itemIds={selectedIds}
            onSuccessModify={() => reloadDataAndResetSelectedRows()}
          />
        )}

        <DialogConfimation
          open={openConfirmDuplicateOrders}
          description="Los destinos seleccionados serán repetidos"
          onConfirm={handleDuplicateOrders}
          onCancel={() => toggleOpenConfirmDuplicateOrders(false)}
          loadingConfirm={loadingDuplicateOrders}
        />
      </DashboardVariantV2CarrierContentActionsContainer>
    );
  };

export default DashboardVariantV2CarrierContentInTransitTabActions;
